/**
 * Ô email + tra cứu tài khoản, hiện kết quả NGAY tại chỗ — OQ-308.
 *
 * **Chủ dự án**: *"tôi muốn chức năng cấp quyền hoạt động tương tự như thêm
 * người dùng cho tên miền gốc ấy, hiện tại đang hoạt động mà không hỏi gì, khi
 * tạo sub-domain cũng nên hoạt động như vậy"*.
 *
 * OQ-307 giải đúng vấn đề — người bấm Tạo cần biết mình sắp cấp quyền cho ai —
 * nhưng giải bằng **hộp thoại thứ hai**, trong khi hệ thống đã có sẵn một lối
 * trả lời cùng câu hỏi ấy mà không chặn đường: tra cứu tại chỗ, kết quả hiện
 * ngay dưới ô nhập.
 *
 * > Một hộp thoại xác nhận trả lời câu hỏi *sau khi* người ta đã quyết. Một ô
 * > tra cứu trả lời nó *trong lúc* họ đang quyết — và không ai phải bấm thêm
 * > nút nào để đi tiếp.
 *
 * Tách khỏi `EmailGrantFlow` thay vì chép sang: hai bản chép sẽ lệch nhau theo
 * thời gian, và chỗ lệch đầu tiên thường là những nhánh hiếm — hết hạn mức tra
 * cứu, email sai định dạng, người đã có quyền. Đúng bài học OQ-304.
 *
 * Giữ nguyên DOM của prototype (`.email-lookup`, `.lookup-card`,
 * `.scope-note.warn`) để hai màn hình trông giống nhau thật, không chỉ giống
 * nhau về ý định.
 */

import { useState } from 'react';
import { ApiError } from '../lib/api';
import { useT } from '../lib/i18n';
import { avatarTone } from '../lib/format';
import { Icon } from './Icon';
import { EMAIL_RE, permApi, type UserLookupView } from '../pages/permissions/api';

export interface EmailLookupState {
  /** Chuỗi email hiện tại, đã chuẩn hoá. */
  email: string;
  /**
   * Ô có chữ hay không — khác `email !== ''` ở chỗ nó đúng cả khi người dùng
   * đang gõ dở một chuỗi chưa phải email (OQ-311).
   *
   * Nơi gọi cần phân biệt "ô trống, tạo nhánh trống là hợp lệ" với "đang gõ dở,
   * chưa xác minh gì" — hai trạng thái mà `email` một mình không nói ra được.
   */
  coChu: boolean;
  /** Kết quả tra cứu gần nhất; `null` khi chưa tra hoặc vừa sửa email. */
  result: UserLookupView | null;
  /** Tìm thấy tài khoản dùng được (đã trừ trường hợp đã có quyền sẵn). */
  found: boolean;
  /** Tra xong và chắc chắn chưa có tài khoản — sẽ phải gửi lời mời. */
  missing: boolean;
  looking: boolean;
}

export function EmailLookupField({
  idPrefix,
  domainId,
  label,
  hintIdle,
  onChange,
}: {
  idPrefix: string;
  /** Kèm theo để máy chủ nói được "người này đã có quyền rồi" (FR-083). */
  domainId?: string;
  label: string;
  hintIdle: string;
  onChange: (state: EmailLookupState) => void;
}): JSX.Element {
  const t = useT();
  const [email, setEmail] = useState('');
  const [looking, setLooking] = useState(false);
  const [result, setResult] = useState<UserLookupView | null>(null);
  const [hint, setHint] = useState(hintIdle);

  const found = result?.found === true && result.alreadyGranted === false;
  const missing = result?.found === false;

  /** Báo lên cha SAU mỗi thay đổi thật, không qua `useEffect`. */
  const bao = (e: string, r: UserLookupView | null, dangTra: boolean): void => {
    onChange({
      email: e.trim().toLowerCase(),
      coChu: e.trim() !== '',
      result: r,
      found: r?.found === true && r.alreadyGranted === false,
      missing: r?.found === false,
      looking: dangTra,
    });
  };

  const xoaKetQua = (e: string): void => {
    setResult(null);
    setHint(hintIdle);
    bao(e, null, false);
  };

  async function traCuu(): Promise<void> {
    const value = email.trim().toLowerCase();
    xoaKetQua(value);

    if (!EMAIL_RE.test(value)) {
      setHint(t('perm.lookupInvalid'));
      return;
    }

    setLooking(true);
    bao(value, null, true);
    try {
      const data = await permApi.lookup(value, domainId);
      setResult(data);
      /*
       * Ba câu khác nhau cho ba kết cục. Gộp thành "đã tra cứu xong" thì người
       * đọc phải tự suy ra điều gì sắp xảy ra — mà đó chính là thứ ô này sinh
       * ra để nói hộ họ.
       */
      if (data.found && data.alreadyGranted) {
        setHint(t('perm.lookupGranted', { name: data.user?.fullName ?? value }));
      } else if (data.found) {
        setHint(t('perm.lookupFound'));
      } else {
        setHint(t('perm.lookupMissing'));
      }
      bao(value, data, false);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'RATE_LIMITED') setHint(t('perm.lookupTooMany'));
      else setHint(err instanceof ApiError ? err.message : t('state.error.title'));
      bao(value, null, false);
    } finally {
      setLooking(false);
    }
  }

  return (
    <>
      <div className="field">
        <label htmlFor={`${idPrefix}-email`}>{label}</label>
        <div className="email-lookup">
          <input
            type="email"
            id={`${idPrefix}-email`}
            placeholder={t('perm.emailPlaceholder')}
            autoComplete="off"
            value={email}
            data-od-id={`${idPrefix}-email`}
            onChange={(event) => {
              setEmail(event.target.value);
              /* Sửa email thì kết quả cũ hết giá trị — giữ lại là nói dối về
                 người sắp nhận quyền. */
              xoaKetQua(event.target.value);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                void traCuu();
              }
            }}
          />
          <button
            className="btn btn-primary"
            type="button"
            disabled={looking}
            onClick={() => void traCuu()}
            data-od-id={`${idPrefix}-lookup`}
          >
            {looking ? t('common.loading') : t('perm.lookup')}
          </button>
        </div>
        <span className="hint">{hint}</span>
      </div>

      {found && result?.user ? (
        <div className="lookup-card" data-od-id={`${idPrefix}-found`}>
          <span className={`avatar sm ${avatarTone(result.user.email)}`}>
            {result.user.initials}
          </span>
          <span className="who">
            <b>{result.user.fullName}</b>
            <span>{result.user.email}</span>
          </span>
          <button
            className="btn btn-sm"
            type="button"
            onClick={() => {
              setEmail('');
              xoaKetQua('');
            }}
          >
            {t('perm.changeEmail')}
          </button>
        </div>
      ) : null}

      {missing ? (
        <div className="scope-note warn" data-od-id={`${idPrefix}-invite`}>
          <Icon name="warn" size={17} />
          <span>{t('perm.inviteWarn', { email: email.trim().toLowerCase() })}</span>
        </div>
      ) : null}
    </>
  );
}
